/**
 * Phase-P — Student Course Payments panel.
 *
 * Reads /api/portal/student/course-payments and renders a clear "PAID IN FULL"
 * / "Partially Paid" / "Payment Due" summary per course. No admin actions.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, Clock, AlertTriangle, Receipt, ExternalLink, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const currencyFmt = (cents, currency = 'AUD') => {
  const amount = (Number(cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return `${currency} ${amount}`;
};

const badgeFor = (status) => {
  if (status === 'paid') {
    return {
      label: 'PAID IN FULL',
      cls: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      Icon: CheckCircle2,
    };
  }
  if (status === 'partially_paid') {
    return { label: 'Partially paid', cls: 'bg-amber-100 text-amber-800 border-amber-300', Icon: Clock };
  }
  if (status === 'overdue') {
    return { label: 'Overdue', cls: 'bg-red-100 text-red-800 border-red-300', Icon: AlertTriangle };
  }
  return { label: 'Payment due', cls: 'bg-slate-100 text-slate-700 border-slate-300', Icon: Clock };
};

export default function StudentCoursePaymentsSection() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await _ax.get('/api/portal/student/course-payments');
      setItems(r.data?.items || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-4" data-testid="student-payments-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading payment status…
      </div>
    );
  }
  if (error) return <div className="text-sm text-red-600" data-testid="student-payments-error">{error}</div>;
  if (!items?.length) return null;

  return (
    <div className="space-y-4" data-testid="student-course-payments">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">Course Payments</h3>
      </div>
      {items.map((c) => {
        const b = badgeFor(c.payment_status);
        const Icon = b.Icon;
        return (
          <div
            key={c.course_id}
            className="rounded-xl border border-slate-200 bg-white p-4"
            data-testid={`payment-course-${c.course_id}`}
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm text-slate-500">{c.subject || 'Course'}</div>
                <div className="text-lg font-semibold text-slate-900">
                  {currencyFmt(c.package_total_cents, c.currency)} <span className="text-slate-400 text-sm font-normal">total</span>
                </div>
              </div>
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase border ${b.cls}`} data-testid={`payment-status-${c.course_id}`}>
                <Icon className="h-3.5 w-3.5" /> {b.label}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
              <div>
                <div className="text-slate-500 text-xs">Paid</div>
                <div className="font-medium text-slate-800">{currencyFmt(c.amount_paid_cents, c.currency)}</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">Balance</div>
                <div className={`font-medium ${c.balance_cents > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                  {currencyFmt(c.balance_cents, c.currency)}
                </div>
              </div>
              {c.next_due && c.payment_status !== 'paid' && (
                <div>
                  <div className="text-slate-500 text-xs">Next due</div>
                  <div className="font-medium text-slate-800">{c.next_due}</div>
                </div>
              )}
              <div>
                <div className="text-slate-500 text-xs">Classroom access</div>
                <div className={`font-medium ${c.classroom_access_enabled ? 'text-emerald-700' : 'text-slate-500'}`}>
                  {c.classroom_access_enabled ? 'Enabled' : 'Locked'}
                </div>
              </div>
            </div>

            {(c.links || []).length > 0 && (
              <details className="mt-3">
                <summary className="text-xs font-semibold uppercase tracking-wider text-slate-500 cursor-pointer">
                  Payment history ({c.links.length})
                </summary>
                <div className="mt-2 space-y-1">
                  {c.links.map((L) => (
                    <div key={L.id} className="flex items-center justify-between text-xs text-slate-700 py-1 border-t border-slate-100">
                      <div>
                        <span className="font-medium">{c.currency} {Number(L.amount || 0).toLocaleString()}</span>
                        <span className="ml-2 text-slate-400">{L.description || '—'}</span>
                        <span className={`ml-2 text-[10px] uppercase font-bold ${L.status === 'paid' ? 'text-emerald-700' : 'text-amber-700'}`}>{L.status}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400">{L.paid_at ? new Date(L.paid_at).toLocaleDateString() : (L.due_date ? `due ${L.due_date}` : '')}</span>
                        {L.receipt_url && (
                          <a href={L.receipt_url} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline inline-flex items-center gap-0.5">
                            <Receipt className="h-3 w-3" /> receipt
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}
